import { useState, useEffect, useCallback } from 'react';
import { db, type ColorFavoriteRecord } from '../lib/database';

export interface UseColorFavoritesResult {
  favorites: ColorFavoriteRecord[];
  loading: boolean;
  error: string | null;
  addFavorite: (hex: string, name?: string, tags?: string[], notes?: string) => Promise<string>;
  removeFavorite: (id: string) => Promise<void>;
  updateFavorite: (id: string, updates: Partial<ColorFavoriteRecord>) => Promise<void>;
  isFavorite: (hex: string) => Promise<boolean>;
  getFavoriteByHex: (hex: string) => ColorFavoriteRecord | undefined;
  refresh: () => Promise<void>;
}

export function useColorFavorites(): UseColorFavoritesResult {
  const [favorites, setFavorites] = useState<ColorFavoriteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFavorites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await db.getFavorites();
      setFavorites(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load favorites');
      console.error('Error loading favorites:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFavorites();
  }, [loadFavorites]);

  const addFavorite = useCallback(async (
    hex: string,
    name?: string,
    tags?: string[],
    notes?: string
  ): Promise<string> => {
    try {
      const id = await db.addFavorite({
        hex: hex.toLowerCase(),
        name,
        tags,
        notes,
      });
      await loadFavorites();
      return id;
    } catch (err) {
      console.error('Error adding favorite:', err);
      throw err;
    }
  }, [loadFavorites]);

  const removeFavorite = useCallback(async (id: string) => {
    try {
      await db.removeFavorite(id);
      await loadFavorites();
    } catch (err) {
      console.error('Error removing favorite:', err);
      throw err;
    }
  }, [loadFavorites]);

  const updateFavorite = useCallback(async (id: string, updates: Partial<ColorFavoriteRecord>) => {
    try {
      await db.updateFavorite(id, updates);
      await loadFavorites();
    } catch (err) {
      console.error('Error updating favorite:', err);
      throw err;
    }
  }, [loadFavorites]);

  const isFavorite = useCallback(async (hex: string): Promise<boolean> => {
    return await db.isFavorite(hex.toLowerCase());
  }, []);

  const getFavoriteByHex = useCallback((hex: string): ColorFavoriteRecord | undefined => {
    return favorites.find(fav => fav.hex.toLowerCase() === hex.toLowerCase());
  }, [favorites]);

  return {
    favorites,
    loading,
    error,
    addFavorite,
    removeFavorite,
    updateFavorite,
    isFavorite,
    getFavoriteByHex,
    refresh: loadFavorites,
  };
}
